import {
  Button,
  useAuthenticator,
  type WithAuthenticatorOptions,
} from "@aws-amplify/ui-react";
import { useMemo } from "react";
import type { LoginOptions } from "../../../../App";
import { css } from "@emotion/react";
import googleLogo from "@assets/google.svg";
import microsoftLogo from "@assets/microsoft.svg";
import { signInWithRedirect } from "@aws-amplify/auth";
import type * as msal from "@azure/msal-browser";
import { PasswordRequirementText } from "./PasswordRequirementText";

export const useAuthenticatorSettings = ({
  email,
  loginOptions,
  setLoginOptions,
  msalInstancePromise,
  setEmail,
  setEmailInput,
  resetAmplify,
}: {
  email: string;
  loginOptions?: LoginOptions;
  setLoginOptions: React.Dispatch<
    React.SetStateAction<LoginOptions | undefined>
  >;
  msalInstancePromise: Promise<msal.PublicClientApplication | null>;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  setEmailInput: React.Dispatch<React.SetStateAction<string>>;
  resetAmplify: () => void;
}) => {
  const authenticatorSettings: WithAuthenticatorOptions = useMemo(
    () => ({
      hideSignUp: true,
      socialProviders: [],
      components: {
        ConfirmResetPassword: {
          Header() {
            return (
              <>
                <h3 className="amplify-heading amplify-heading--3">
                  Reset Password
                </h3>
                <PasswordRequirementText />
              </>
            );
          },
        },
        ForceNewPassword: {
          Header() {
            return (
              <>
                <h3 className="amplify-heading amplify-heading--3">
                  Change Password
                </h3>
                <PasswordRequirementText />
              </>
            );
          },
        },
        ForgotPassword: {
          Header() {
            return (
              <>
                <h3 className="amplify-heading amplify-heading--3">
                  Reset Password
                </h3>
                <PasswordRequirementText />
              </>
            );
          },
        },
        SignIn: {
          Header() {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: `16px 32px ${loginOptions?.password ? "0" : "32px"} 32px`,
                  marginBottom: loginOptions?.password ? "-24px" : "0",
                  gap: "12px",
                }}
              >
                {loginOptions?.microsoft && (
                  <button
                    className="amplify-button amplify-field-group__control federated-sign-in-button"
                    type="button"
                    style={{ gap: "1rem", width: "100%" }}
                    onClick={async () => {
                      const msalInstance = await msalInstancePromise;
                      if (!msalInstance) {
                        return;
                      }
                      await msalInstance.loginRedirect({
                        redirectUri: window.location.origin,
                        scopes: ["email"],
                        prompt: "select_account",
                      });
                    }}
                  >
                    <img src={microsoftLogo} alt="Microsoft" />
                    <span className="amplify-text">Sign In with Microsoft</span>
                  </button>
                )}
                {loginOptions?.google && (
                  <button
                    className="amplify-button amplify-field-group__control federated-sign-in-button"
                    type="button"
                    style={{ gap: "1rem", width: "100%" }}
                    onClick={() => {
                      signInWithRedirect({
                        provider: "Google",
                      });
                    }}
                  >
                    <img
                      className="amplify-icon federated-sign-in-icon"
                      src={googleLogo}
                      alt="Google"
                    />
                    <span className="amplify-text">Sign In with Google</span>
                  </button>
                )}

                {(loginOptions?.google || loginOptions?.microsoft) &&
                  loginOptions?.password && (
                    <hr
                      aria-orientation="horizontal"
                      className="amplify-divider amplify-divider--horizontal amplify-divider--small"
                      data-label="or"
                    ></hr>
                  )}
              </div>
            );
          },
          Footer() {
            const { toForgotPassword } = useAuthenticator();
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Button
                  css={css`
                    align-self: flex-start;
                  `}
                  className="backButton"
                  onClick={() => {
                    setEmail("");
                    setEmailInput("");
                    setLoginOptions(undefined);
                    resetAmplify();
                  }}
                >
                  Back
                </Button>
                {loginOptions?.password && (
                  <Button
                    css={css`
                      align-self: flex-end;
                    `}
                    variation="link"
                    onClick={toForgotPassword}
                  >
                    Forgot password?
                  </Button>
                )}
              </div>
            );
          },
        },
      },
      formFields: {
        signIn: {
          username: {
            label: "Email",
            defaultValue: email,
            isReadOnly: true,
            placeholder: "Enter your email",
          },
          ...(loginOptions?.password
            ? {}
            : {
                password: {
                  isReadOnly: true,
                },
              }),
        },
        forgotPassword: {
          username: {
            label: "Email",
            placeholder: "Enter your email",
          },
        },
      },
    }),
    [
      email,
      loginOptions?.google,
      loginOptions?.microsoft,
      loginOptions?.password,
      msalInstancePromise,
      resetAmplify,
      setEmail,
      setEmailInput,
      setLoginOptions,
    ],
  );

  return authenticatorSettings;
};
